import React, { useCallback, useEffect, useState } from 'react';
import { Button, Flex, Tooltip } from 'antd';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import editIcon from 'assets/images/table-icons/edit_green.svg';
import pdfIcon from 'assets/images/icons/pdf_red.svg';
import pdfIconDisable from 'assets/images/icons/pdf_white.svg';
import { transformRangeDataToAmountArray } from './GrossRent';
import { constantFormValues } from '../../root';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { toastConfig } from 'config';
import { processArrays, utcConverter } from '../LeaseUtils/helper';

/**
 * ActionButtons component handles all action buttons for the lease management system
 * with flexibility to work in both Create and Edit modes.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.screen - Current screen mode ('CREATE', 'EDIT', or 'VIEW')
 * @param {function} props.handleAddRow - Function to handle adding a new row
 * @param {function} props.handlePreview - Function to handle preview/schedule generation
 * @param {function} [props.handleCreateClick] - Function for Create mode submission
 * @param {function} [props.handleUpdateButtonActions] - Function for Edit mode submission
 * @param {boolean} props.isLoadDataButtonDisabled - Flag to determine if Load Data button should be disabled
 * @param {boolean} [props.isPopulateScheduleDisabled] - Create mode: flag for preview button
 * @param {boolean} [props.newRowAdded] - Edit mode: flag that enables preview when true
 * @param {boolean} [props.isSubmitButtonDisable] - Create mode: flag for submit button
 * @param {boolean} [props.isUpdateButtonDisabled] - Edit mode: flag for update button
 * @param {boolean} props.isScheduleLoading - Flag to indicate if schedule is loading
 * @param {Object} props.viewData - View data for the lease record (needed for VIEW mode)
 * @param {Object} props.state - Current application state object
 * @param {function} props.navigate - Navigation function for routing
 * @param {function} props.handleSingleLeaseDeactivate - Function to handle lease deactivation
 * @param {boolean} props.areAllRecordsRecorded - Flag to check if all records are recorded
 * @param {function} props.isLeaseLifeCycleOver - Function to check if lease lifecycle is over
 * @param {Object} [props.selectHistory] - Function to select history record
 *
 * @returns {JSX.Element} The action buttons section
 */
const ActionButtons = ({
	form,
	screen,
	sendControlDataWithPayload,
	setMissingFields,
	handleAddRow,
	statusData,
	setCalculateData,
	initialFormValues,
	leaseChanges,
	setIdentityData,
	// handlePreview,
	handleCreateClick,
	handleUpdateButtonActions,
	isLoadDataButtonDisabled,
	isPopulateScheduleDisabled,
	newRowAdded,
	// isSubmitButtonDisable,
	// isUpdateButtonDisabled,
	isScheduleLoading,
	viewData,
	state,
	navigate,
	handleSingleLeaseDeactivate,
	// areAllRecordsRecorded,
	isLeaseLifeCycleOver,
	selectHistory,
	leaseControlData,
	setIsPopulateClick,
	setLeaseControlData,
	sendControlData,
}) => {
	const theme = useTheme();

	// Determine the appropriate handlers and flags based on screen mode
	const handleSubmitAction = () => {
		if (screen === 'CREATE') {
			if (handleCreateClick) handleCreateClick();
		} else {
			if (handleUpdateButtonActions) handleUpdateButtonActions();
		}
	};
	const [areAllRecordsRecorded, setAreAllRecordsRecorded] = useState(false);
	// Determine button disabled states based on screen mode
	// const isPreviewButtonDisabled = screen === 'CREATE' ? isPopulateScheduleDisabled : !newRowAdded;
	// console.log('🚀 ~ isPreviewButtonDisabled:', isPreviewButtonDisabled);

	// const isSubmitButtonDisabled = screen === 'CREATE' ? isSubmitButtonDisable : isUpdateButtonDisabled;
	const handlePreviewCreatePage = async () => {
		let payloadData = {};
		if (screen === 'CREATE') {
			// const changeData = form.getFieldValue('changes');
			// console.log('🚀 ~ handlePreviewCreatePage ~ changeData:', changeData);
			// setIdentityData({
			// 	leaseType: form.getFieldValue('leaseType'),
			// 	leaseTitle: form.getFieldValue('leaseTitle'),
			// 	lessorName: form.getFieldValue('lessorName'),
			// 	paymentFrequency: form.getFieldValue('paymentFrequency'),
			// 	paymentModality: form.getFieldValue('paymentModality'),
			// 	advanceDuration: form.getFieldValue('advanceDuration'),
			// 	advanceAmount: form.getFieldValue('advanceAmount'),
			// 	grossRent: form.getFieldValue('grossRent'),
			// 	leaseTerm: form.getFieldValue('leaseTerm'),
			// 	leaseStartDate: form.getFieldValue('leaseStartDate'),
			// 	leaseEndDate: form.getFieldValue('leaseEndDate'),
			// 	escalationPercentage: form.getFieldValue('escalationPercentage'),
			// 	initialDiscountRate: form.getFieldValue('initialDiscountRate'),
			// 	rightOfUseAssetValue: form.getFieldValue('rightOfUseAssetValue'),
			// 	glleaseLiability: form.getFieldValue('glleaseLiability'),
			// 	costCenterInterestExpense: form.getFieldValue('costCenterInterestExpense'),
			// 	glInterestExpense: form.getFieldValue('glInterestExpense'),
			// 	leaseExpenseCostCenter: form.getFieldValue('leaseExpenseCostCenter'),
			// 	leaseExpense: form.getFieldValue('leaseExpense'),
			// 	leasePayableControlAccount: form.getFieldValue('leasePayableControlAccount'),
			// 	glAdvanceRent: form.getFieldValue('glAdvanceRent'),
			// 	depreciationRoUCostCenter: form.getFieldValue('depreciationRoUCostCenter'),
			// 	depreciationRoU: form.getFieldValue('depreciationROU'),
			// 	accumDepreciationRoU: form.getFieldValue('accumDepreciationROU'),
			// 	glBankAccount: form.getFieldValue('glBankAccount'),
			// 	securityDepositHead: form.getFieldValue('securityDepositHead'),
			// 	accountsPayableHead: form.getFieldValue('accountsPayableHead'),
			// 	changes: form.getFieldValue('changes'),
			// });
			setIdentityData(form.getFieldsValue());
		}

		if (screen === 'CREATE') {
			const startDate = form.getFieldValue('changes')[0]?.inputChangeEffectiveDate;

			const startYear = startDate?.year();

			const startMonth = startDate?.month();

			const startDay = startDate?.date();

			const grossRentList = form.getFieldValue('grossRentList');
			const advanceAmountList = form.getFieldValue('advanceAmountList');
			const grossRents = transformRangeDataToAmountArray(grossRentList);
			const advanceAmounts = transformRangeDataToAmountArray(advanceAmountList);
			const { processedGrossRents, processedAdvanceAmounts } = processArrays(grossRents, advanceAmounts);
			payloadData = {
				advanceAmount: form.getFieldValue('advanceAmount'),
				grossRent: form.getFieldValue('grossRent'),
				leaseTerm: form.getFieldValue('leaseTerm'),
				discountRate: form.getFieldValue(['changes', 0, 'inputChangeDiscountRate']),
				startYear: `${startYear}`,
				escalation: form.getFieldValue('escalationPercentage') ?? 0,
				startMonth: String(startMonth + 1),
				paymentFrequency: form.getFieldValue('paymentFrequency'),
				paymentTime: form.getFieldValue('paymentModality'),
				advanceDuration: advanceAmounts?.length,
				startDay: String(startDay),
				upfrontLeasePayment: form.getFieldValue(constantFormValues?.upfrontLeasePayment?.key),
				initialDirectCost: form.getFieldValue(constantFormValues?.initialDirectCost?.key),
				securityDeposit: form.getFieldValue(constantFormValues?.securityDeposit?.key),
				endOfFinancialYear: form.getFieldValue(constantFormValues?.endOfFinancialYear?.key) ?? dayjs().month(11).date(31),
				advanceAmounts: processedAdvanceAmounts,
				grossRents: processedGrossRents,
				changes: leaseChanges,
			};
		} else {
			payloadData = {
				advanceAmount: initialFormValues?.advanceAmount,
				grossRent: statusData?.changes[0]?.inputChangeGrossAmount,
				leaseTerm: statusData?.changes[0]?.inputChangeLeaseTerm,
				discountRate: statusData?.changes[0]?.inputChangeDiscountRate,
				startYear: String(dayjs(statusData?.leaseStartDate).$y),
				escalation: statusData?.changes[0]?.escalationPercentage,
				startMonth: String(dayjs(statusData?.leaseStartDate).month() + 1),
				paymentFrequency: statusData?.changes[0]?.paymentFrequency,
				paymentTime: statusData?.changes[0]?.paymentTime,
				advanceDuration: viewData?.advanceDuration,
				upfrontLeasePayment: statusData?.upfrontLeasePayment,
				initialDirectCost: statusData?.initialDirectCost,
				securityDeposit: statusData?.securityDeposit,
				endOfFinancialYear: statusData?.endOfFinancialYear,
				advanceAmounts: statusData?.advanceAmounts,
				grossRents: statusData?.grossRents,
				changes: (form.getFieldValue('changes') ?? []).slice(1),
			};
		}

		// console.log('payloadData', payloadData);
		setCalculateData(payloadData);

		const requiredFields = [
			// 'grossRent',
			'leaseTerm',
			'discountRate',
			'advanceAmount',
			// 'escalation',
			'paymentFrequency',
			'paymentTime',
			'advanceDuration',
			'startYear',
			'startMonth',
			'startDay',
		];
		const emptyFields = requiredFields.filter(
			(field) => payloadData[field] === '' || payloadData[field] === null || payloadData[field] === undefined
		);

		setMissingFields(emptyFields.length);
		// console.log('emptyFields', emptyFields);

		if (emptyFields?.length > 0) {
			return;
		}

		const convertJson = JSON.stringify(payloadData);
		const convertBase64 = btoa(convertJson);

		const formattedPayload = {
			data: convertBase64,
			processCode: state?.processCode,
			scriptName: 'lease',
			payloadData: payloadData,
		};
		try {
			await sendControlData(formattedPayload).then((res) => {
				setLeaseControlData(res?.data?.responseData);
			});
		} catch (error) {
			toast.error('Failed! Action not completed', toastConfig);
		}
		setIsPopulateClick(true);
	};
	function getEffectiveLengthWithoutTrailingZeros(arr) {
		let length = arr.length;

		// Decrease length while there are trailing zeros
		while (length > 0 && arr[length - 1] === 0) {
			length--;
		}

		return length;
	}
	const handlePreviewEditPage = useCallback(async () => {
		setLeaseControlData([]);

		let payloadData = {};
		const effectiveDate = form.getFieldValue('changes')[0]?.inputChangeEffectiveDate;
		// console.log('🚀 ~ handlePreviewEditPage ~ effectiveDate:', effectiveDate);

		const startDay = dayjs(effectiveDate).date();
		const startMonth = dayjs(effectiveDate).month() + 1;
		const startYear = dayjs(effectiveDate).year();
		const leaseStartDate = dayjs(form.getFieldValue('leaseStartDate'));

		const formChanges = form.getFieldValue('changes') ?? [];

		// Process changes, starting from the second element (index 1)
		const processedChanges = formChanges.slice(1).map((change, index, array) => {
			// Create a copy to avoid mutating the original
			let updatedChange = { ...change };

			// console.log('🚀 ~ processedChanges ~ updatedChange:', updatedChange);
			if (updatedChange.inputChangeEffectiveDate) {
				const effectiveDate = dayjs(updatedChange.inputChangeEffectiveDate);
				let monthDifference = (effectiveDate.year() - leaseStartDate.year()) * 12 + (effectiveDate.month() - leaseStartDate.month()) + 1;

				if (monthDifference < 0) {
					monthDifference = 0;
				}

				updatedChange.inputChangeMonth = monthDifference;
				updatedChange.inputChangeEffectiveDate = utcConverter(updatedChange.inputChangeEffectiveDate);
				const advanceAmountPeriod = getEffectiveLengthWithoutTrailingZeros(updatedChange?.inputChangeAdvanceAmounts);
				updatedChange.inputChangeAmortizationPeriod = advanceAmountPeriod;
			}

			// Only add the modal data to the last element of the processed changes
			if (index === array.length - 1) {
				// Get current form values for modal data
				// const advanceAmounts = form.getFieldValue('advanceAmounts');
				// console.log('🚀 ~ processedChanges ~ advanceAmounts:', advanceAmounts);
				// const grossRents = form.getFieldValue('grossRents');
				// console.log('🚀 ~ processedChanges ~ grossRents:', grossRents);
				const grossRentList = form.getFieldValue('grossRentList');
				const advanceAmountList = form.getFieldValue('advanceAmountList');
				const grossRents = transformRangeDataToAmountArray(grossRentList);
				const advanceAmounts = transformRangeDataToAmountArray(advanceAmountList);
				const { processedGrossRents, processedAdvanceAmounts } = processArrays(grossRents, advanceAmounts);
				updatedChange.inputChangeAmortizationPeriod = advanceAmountList[advanceAmountList?.length - 1]?.to;
				// Only set if they exist
				if (processedAdvanceAmounts) {
					updatedChange.inputChangeAdvanceAmounts = processedAdvanceAmounts;
				}

				if (processedGrossRents) {
					updatedChange.inputChangeGrossRents = processedGrossRents;
				}

				// console.log('Setting modal data on the last change element:', updatedChange);
			}

			return updatedChange;
		});

		// console.log('Processed changes:', processedChanges);

		const advanceAmountData = processArrays(
			statusData?.changes[0]?.inputChangeGrossRents,
			statusData?.changes[0]?.inputChangeAdvanceAmounts
		).processedAdvanceAmounts;
		payloadData = {
			effectiveDate: utcConverter(dayjs(statusData?.changes[0].inputChangeEffectiveDate)),
			advanceAmount: advanceAmountData.reduce((sum, currentValue) => {
				// Handle non-numeric values by converting them to numbers or using 0
				const valueToAdd = typeof currentValue === 'number' ? currentValue : isNaN(Number(currentValue)) ? 0 : Number(currentValue);

				return sum + valueToAdd;
			}, 0),
			grossRent: statusData?.changes[0]?.inputChangeGrossAmount || 0,
			leaseTerm: statusData?.changes[0]?.inputChangeLeaseTerm || 0,
			discountRate: statusData?.changes[0]?.inputChangeDiscountRate,
			startYear: String(startYear),
			startMonth: String(startMonth),
			startDay: String(startDay),
			escalation: statusData?.changes[0]?.inputChangeEscalation || 0,
			paymentFrequency: statusData?.changes[0]?.paymentFrequency,
			paymentTime: statusData?.changes[0]?.paymentTime,
			advanceDuration: statusData?.advanceDuration,
			upfrontLeasePayment: statusData?.upfrontLeasePayment,
			securityDeposit: statusData?.securityDeposit,
			initialDirectCost: statusData?.initialDirectCost,
			endOfFinancialYear: utcConverter(dayjs(statusData?.endOfFinancialYear)),
			// advanceAmounts: statusData?.changes[0]?.inputChangeAdvanceAmounts,
			// grossRents: statusData?.changes[0]?.inputChangeGrossRents,
			advanceAmounts: advanceAmountData,
			grossRents: processArrays(statusData?.changes[0]?.inputChangeGrossRents, statusData?.changes[0]?.inputChangeAdvanceAmounts)
				.processedGrossRents,
			changes: processedChanges,
		};

		try {
			const response = await sendControlDataWithPayload(payloadData);
			setLeaseControlData(response?.data?.responseData);
			setIsPopulateClick(true);
		} catch (error) {
			console.error('Error in preview:', error);
		}
	}, [initialFormValues, statusData, form, sendControlDataWithPayload]);
	useEffect(() => {
		if (leaseControlData) {
			const isAllRecordsRecorded = leaseControlData.every((item) => item?.processStatus === 'Recorded');
			setAreAllRecordsRecorded(isAllRecordsRecorded);
		}
	}, [leaseControlData]);

	return (
		<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '4px', pt: '15px' }}>
			{screen !== 'VIEW' ? (
				<>
					{/* Left side buttons */}
					<Flex justify="left" gap={9}>
						<Tooltip title={isLoadDataButtonDisabled ? 'Required fields are unchanged' : ''}>
							<Button
								// disabled={isLoadDataButtonDisabled}
								type="primary"
								onClick={handleAddRow}
								style={{ marginBottom: 10, marginTop: 10 }}
							>
								Load Data
							</Button>
						</Tooltip>
						<Button
							// disabled={isPreviewButtonDisabled}
							type="primary"
							onClick={screen?.toLowerCase() === 'create' ? handlePreviewCreatePage : handlePreviewEditPage}
							style={{ marginBottom: 10, marginTop: 10 }}
							loading={isScheduleLoading}
						>
							{screen === 'CREATE' ? 'Run Lease Schedule' : 'Update Lease Schedule'}
						</Button>
					</Flex>

					{/* Right side button */}
					<Button
						// disabled={isSubmitButtonDisabled}
						type="primary"
						onClick={handleSubmitAction}
						style={{
							// backgroundColor: isSubmitButtonDisabled ? '#b4bdbb' : theme.palette.lynTheme.main,
							backgroundColor: theme.palette.lynTheme.main,
							marginBottom: 10,
							marginTop: 10,
							// cursor: isSubmitButtonDisabled ? 'not-allowed' : 'pointer',
						}}
					>
						{screen === 'CREATE' ? 'Submit' : 'Update'}
					</Button>
				</>
			) : (
				// VIEW mode buttons
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
					{viewData && (
						<>
							<Button
								type="primary"
								style={{
									fontSize: '13px',
									fontWeight: '500',
									borderRadius: '6px',
									marginRight: '3px',
									display: 'flex',
									alignItems: 'center',
									gap: '6px',
									...(viewData?.history?.length === 0 ? { backgroundColor: '#6c757d' } : {}),
								}}
								onClick={(e) => {
									e.stopPropagation();
									if (selectHistory) {
										selectHistory({ ...viewData, ...viewData?.data, id: viewData?._id });
									} else if (viewData?.selectHistory) {
										viewData.selectHistory({ ...viewData, ...viewData?.data, id: viewData?._id });
									}
								}}
							>
								<img src={viewData?.history?.length > 1 ? pdfIcon : pdfIconDisable} width={16} height={16} alt="view" />
								Change Log
							</Button>
							<Button
								// disabled={viewData?.activationStatus?.toLowerCase() === 'closed'}
								style={{
									display: 'flex',
									alignItems: 'center',
									fontSize: '13px',
									fontWeight: '500',
									borderRadius: '6px',
									margin: '1px',
									marginLeft: '6px',
								}}
								icon={<img src={editIcon} width={16} height={16} alt="edit" />}
								onClick={(e) => {
									e.stopPropagation();
									if (navigate && state) {
										navigate('/static/lease-record-management-v2/edit', {
											state: state,
										});
									}
								}}
							>
								Edit
							</Button>

							{areAllRecordsRecorded &&
								isLeaseLifeCycleOver &&
								isLeaseLifeCycleOver() &&
								(viewData?.activationStatus !== 'Closed' ? (
									<Button
										style={{
											display: 'flex',
											alignItems: 'center',
											fontSize: '13px',
											fontWeight: '500',
											borderRadius: '6px',
											margin: '1px',
											marginLeft: '6px',
										}}
										onClick={(e) => {
											e.stopPropagation();
											if (handleSingleLeaseDeactivate) {
												handleSingleLeaseDeactivate('Closed');
											}
										}}
									>
										Close
									</Button>
								) : (
									<Button
										style={{
											display: 'flex',
											alignItems: 'center',
											fontSize: '13px',
											fontWeight: '500',
											borderRadius: '6px',
											margin: '1px',
											marginLeft: '6px',
										}}
										onClick={(e) => {
											e.stopPropagation();
											if (handleSingleLeaseDeactivate) {
												handleSingleLeaseDeactivate('Reopened');
											}
										}}
									>
										Reopen
									</Button>
								))}
						</>
					)}
				</Box>
			)}
		</Box>
	);
};

export default ActionButtons;
